import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Award,
  CheckCircle2,
  ChevronDown,
  Circle,
  ExternalLink,
  FileText,
  Link2,
  PlayCircle,
  type LucideIcon,
} from 'lucide-react'
import { apiJson } from '../api'

interface Lesson {
  id: string
  title: string
  orderIndex: number
  contentType: 'video' | 'article' | 'document' | 'link'
  contentUrl: string | null
  body: string | null
  durationMinutes: number
  completed: boolean
}

interface Module {
  id: string
  title: string
  orderIndex: number
  lessons: Lesson[]
}

interface ContentView {
  courseId: string
  modules: Module[]
  totalLessons: number
  completedLessons: number
}

interface CompleteResult {
  progress: number
  completedLessons: number
  totalLessons: number
  courseCompleted: boolean
}

const typeIcon: Record<Lesson['contentType'], LucideIcon> = {
  video: PlayCircle,
  article: FileText,
  document: FileText,
  link: Link2,
}

const typeLabel: Record<Lesson['contentType'], string> = {
  video: 'Video',
  article: 'Article',
  document: 'Document',
  link: 'Link',
}

export default function CoursePlayer() {
  const { id: courseId } = useParams()
  const [content, setContent] = useState<ContentView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null)
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({})
  const [acting, setActing] = useState(false)
  const [justCompletedCourse, setJustCompletedCourse] = useState(false)

  const flatLessons = useMemo(() => (content?.modules ?? []).flatMap((m) => m.lessons), [content])

  const load = useCallback(async () => {
    try {
      const c = await apiJson<ContentView>(`/courses/${courseId}/content`)
      setContent(c)
      setError('')
      if (!activeLessonId) {
        // open the first incomplete lesson, else the first lesson
        const flat = c.modules.flatMap((m) => m.lessons)
        const target = flat.find((l) => !l.completed) ?? flat[0]
        if (target) {
          setActiveLessonId(target.id)
          const mod = c.modules.find((m) => m.lessons.some((l) => l.id === target.id))
          if (mod) setOpenModules({ [mod.id]: true })
        } else {
          setOpenModules(Object.fromEntries(c.modules.map((m) => [m.id, true])))
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content')
    } finally {
      setLoading(false)
    }
  }, [courseId, activeLessonId])

  useEffect(() => {
    setLoading(true)
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId])

  const activeLesson = flatLessons.find((l) => l.id === activeLessonId) ?? null
  const activeIndex = flatLessons.findIndex((l) => l.id === activeLessonId)
  const nextLesson = activeIndex >= 0 ? flatLessons[activeIndex + 1] : undefined
  const progressPct = content && content.totalLessons > 0
    ? Math.round((content.completedLessons / content.totalLessons) * 100)
    : 0

  const applyResult = (r: CompleteResult) => {
    if (!content) return
    // optimistic re-render from server result; then reload for truth
    if (r.courseCompleted) setJustCompletedCourse(true)
    load()
  }

  const toggleLesson = async (lesson: Lesson) => {
    setActing(true)
    setError('')
    try {
      const r = await apiJson<CompleteResult>(
        `/lessons/${lesson.id}/${lesson.completed ? 'incomplete' : 'complete'}`,
        { method: 'POST' },
      )
      applyResult(r)
      return r
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed')
      return null
    } finally {
      setActing(false)
    }
  }

  const completeAndNext = async () => {
    if (!activeLesson) return
    const r = await toggleLesson(activeLesson)
    if (r && nextLesson) {
      setActiveLessonId(nextLesson.id)
      const mod = content?.modules.find((m) => m.lessons.some((l) => l.id === nextLesson.id))
      if (mod) setOpenModules((o) => ({ ...o, [mod.id]: true }))
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  if (!content) {
    return (
      <div className="space-y-4">
        <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
          <ArrowLeft size={15} /> Back to course
        </Link>
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" /> {error || 'No content available'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
          <ArrowLeft size={15} /> Back to course
        </Link>
        <span className="text-xs font-semibold text-slate-500">
          {content.completedLessons}/{content.totalLessons} lessons · {progressPct}%
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}

      {justCompletedCourse && (
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <Award size={22} className="mt-0.5 shrink-0 text-emerald-600" />
          <div>
            <p className="text-sm font-bold text-emerald-800">Course completed — well done!</p>
            <p className="mt-0.5 text-xs text-emerald-600">
              Every lesson is finished. Check the Certifications tab if this course grants one.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-5">
        {/* sidebar: module tree */}
        <div className="space-y-3 lg:col-span-2">
          {/* progress bar */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-500">Course progress</span>
              <span className="font-bold text-slate-800">{progressPct}%</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full transition-all ${progressPct === 100 ? 'bg-emerald-500' : 'bg-gradient-to-r from-brand-500 to-brand-600'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {content.modules.map((m, mi) => {
            const doneInModule = m.lessons.filter((l) => l.completed).length
            const open = openModules[m.id] ?? mi === 0
            return (
              <div key={m.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <button
                  onClick={() => setOpenModules((o) => ({ ...o, [m.id]: !open }))}
                  className="flex w-full items-center justify-between gap-2 px-4 py-3.5 text-left"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold tracking-tight">
                      <span className="mr-1.5 text-slate-400">{mi + 1}.</span>
                      {m.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-400">
                      {doneInModule}/{m.lessons.length} complete
                    </p>
                  </div>
                  <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
                </button>
                {open && (
                  <div className="border-t border-slate-100">
                    {m.lessons.map((l) => {
                      const Icon = typeIcon[l.contentType]
                      const active = l.id === activeLessonId
                      return (
                        <button
                          key={l.id}
                          onClick={() => setActiveLessonId(l.id)}
                          className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                            active ? 'bg-brand-50/70' : 'hover:bg-slate-50'
                          }`}
                        >
                          {l.completed ? (
                            <CheckCircle2 size={16} className="shrink-0 text-emerald-500" />
                          ) : (
                            <Circle size={16} className="shrink-0 text-slate-300" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className={`truncate text-[13px] ${active ? 'font-semibold text-brand-800' : 'text-slate-600'}`}>
                              {l.title}
                            </p>
                            <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                              <Icon size={11} /> {typeLabel[l.contentType]}
                              {l.durationMinutes > 0 && ` · ${l.durationMinutes} min`}
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* main: lesson content */}
        <div className="lg:col-span-3">
          {activeLesson ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-6 py-5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
                    {typeLabel[activeLesson.contentType]}
                  </span>
                  {activeLesson.durationMinutes > 0 && (
                    <span className="text-[11px] text-slate-400">{activeLesson.durationMinutes} min</span>
                  )}
                  {activeLesson.completed && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                      <CheckCircle2 size={11} /> Done
                    </span>
                  )}
                </div>
                <h1 className="mt-2 text-lg font-bold tracking-tight">{activeLesson.title}</h1>
              </div>

              <div className="px-6 py-6">
                {activeLesson.contentType === 'article' ? (
                  <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">
                    {activeLesson.body ?? 'No content.'}
                  </p>
                ) : (
                  <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center">
                    {(() => {
                      const Icon = typeIcon[activeLesson.contentType]
                      return <Icon size={36} className="text-brand-400" />
                    })()}
                    <p className="mt-3 text-sm font-semibold">
                      {activeLesson.contentType === 'video' ? 'Video lesson' : activeLesson.contentType === 'document' ? 'Document' : 'External resource'}
                    </p>
                    <p className="mt-1 max-w-sm break-all text-xs text-slate-400">{activeLesson.contentUrl}</p>
                    {activeLesson.contentUrl && (
                      <a
                        href={activeLesson.contentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-xs font-semibold text-white shadow-md shadow-brand-500/25 hover:brightness-110"
                      >
                        <ExternalLink size={13} /> Open {typeLabel[activeLesson.contentType].toLowerCase()}
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
                <button
                  onClick={() => toggleLesson(activeLesson)}
                  disabled={acting}
                  className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-40 ${
                    activeLesson.completed
                      ? 'border border-slate-200 text-slate-500 hover:bg-slate-50'
                      : 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-500/25 hover:brightness-110'
                  }`}
                >
                  {activeLesson.completed ? 'Mark as not done' : 'Mark complete'}
                </button>
                {nextLesson && (
                  <button
                    onClick={completeAndNext}
                    disabled={acting}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-40"
                  >
                    {activeLesson.completed ? 'Next lesson' : 'Complete & next'} <ArrowRight size={15} />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-16 text-center shadow-sm">
              <PlayCircle size={28} className="text-slate-300" />
              <p className="mt-3 text-sm font-semibold">Pick a lesson to start</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
